import { apiClient } from '../client';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';

/**
 * Client for `/api/comms/announcements` (Stream E / ADR-027 §3.1).
 * Mirrors `server/src/routes/comms/announcements.routes.ts` +
 * `modules/comms/announcements/announcements.service.ts` — org-wide /
 * department announcements with lazy read+ack receipts.
 *
 * The `{data}` envelope is unwrapped here; dates arrive as ISO strings.
 */

export type AnnouncementAudience = 'org' | 'department';

/** My own receipt on an announcement (null until I first read/ack it). */
export interface MyReceipt {
  readAt: string | null;
  acknowledgedAt: string | null;
}

export interface Announcement {
  id: string;
  title: string;
  /** TipTap doc — render with `TipTapRenderer`, edit with `RichTextEditor`. */
  bodyJson: TipTapDoc;
  audience: AnnouncementAudience;
  departmentId: string | null;
  requiresAck: boolean;
  authorMembershipId: string | null;
  authorNameSnapshot: string;
  publishedAt: string;
  emailedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  myReceipt: MyReceipt | null;
}

export interface AnnouncementPage {
  items: Announcement[];
  hasMore: boolean;
}

/** The raw receipt row returned by the read/acknowledge endpoints. */
export interface AnnouncementReceiptRow {
  id: string;
  announcementId: string;
  organizationId: string;
  membershipId: string | null;
  nameSnapshot: string;
  emailSnapshot: string;
  readAt: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptPerson {
  membershipId: string | null;
  name: string;
  email: string;
  readAt: string | null;
  acknowledgedAt: string | null;
}

export interface PendingPerson {
  membershipId: string;
  name: string;
  email: string;
}

export interface AnnouncementReceiptsBreakdown {
  read: ReceiptPerson[];
  acknowledged: ReceiptPerson[];
  pending: PendingPerson[];
}

// ── List ────────────────────────────────────────────────────────────────

export interface ListAnnouncementsParams {
  limit?: number;
  /** Cursor: only announcements published strictly before this ISO time. */
  before?: string;
  /** Posters only (silently ignored server-side otherwise). */
  includeArchived?: boolean;
}

/** Params → query string, matching the router's coercion rules. */
export function buildAnnouncementListParams(params: ListAnnouncementsParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.before) sp.set('before', params.before);
  if (params.includeArchived) sp.set('includeArchived', 'true');
  return sp;
}

export async function listAnnouncements(params: ListAnnouncementsParams = {}): Promise<AnnouncementPage> {
  const qs = buildAnnouncementListParams(params).toString();
  const res = await apiClient.get<{ data: AnnouncementPage }>(
    `/api/comms/announcements${qs ? `?${qs}` : ''}`,
  );
  return res.data.data;
}

// ── Create / update / archive ───────────────────────────────────────────

export interface CreateAnnouncementInput {
  title: string;
  bodyJson: TipTapDoc;
  audience: AnnouncementAudience;
  /** Required iff audience === 'department'. */
  departmentId?: string | null;
  requiresAck?: boolean;
  emailOnPublish?: boolean;
}

export async function createAnnouncement(input: CreateAnnouncementInput): Promise<Announcement> {
  const res = await apiClient.post<{ data: Announcement }>('/api/comms/announcements', input);
  return res.data.data;
}

export async function getAnnouncement(id: string): Promise<Announcement> {
  const res = await apiClient.get<{ data: Announcement }>(
    `/api/comms/announcements/${encodeURIComponent(id)}`,
  );
  return res.data.data;
}

/** Title / body / requiresAck only — audience can never be retargeted. */
export interface UpdateAnnouncementInput {
  title?: string;
  bodyJson?: TipTapDoc;
  requiresAck?: boolean;
}

export async function updateAnnouncement(id: string, patch: UpdateAnnouncementInput): Promise<Announcement> {
  const res = await apiClient.put<{ data: Announcement }>(
    `/api/comms/announcements/${encodeURIComponent(id)}`,
    patch,
  );
  return res.data.data;
}

export async function archiveAnnouncement(id: string): Promise<void> {
  await apiClient.delete(`/api/comms/announcements/${encodeURIComponent(id)}`);
}

// ── Read / acknowledge (recipient-only, idempotent) ─────────────────────

export async function markAnnouncementRead(id: string): Promise<AnnouncementReceiptRow> {
  const res = await apiClient.post<{ data: AnnouncementReceiptRow }>(
    `/api/comms/announcements/${encodeURIComponent(id)}/read`,
    {},
  );
  return res.data.data;
}

export async function acknowledgeAnnouncement(id: string): Promise<AnnouncementReceiptRow> {
  const res = await apiClient.post<{ data: AnnouncementReceiptRow }>(
    `/api/comms/announcements/${encodeURIComponent(id)}/acknowledge`,
    {},
  );
  return res.data.data;
}

// ── Receipts breakdown (poster surface) ─────────────────────────────────

export async function listAnnouncementReceipts(id: string): Promise<AnnouncementReceiptsBreakdown> {
  const res = await apiClient.get<{ data: AnnouncementReceiptsBreakdown }>(
    `/api/comms/announcements/${encodeURIComponent(id)}/receipts`,
  );
  return res.data.data;
}
